import { Container } from "@mui/material";
import LazyTable, {
  DEFAULT_ROWS_PER_PAGE,
  TableCellType,
  TableSortType,
} from "components/LazyTable";
import OpenLinkInNewTabContextMenuItem from "components/OpenInNewTabContextMenuItem";
import SearchBoxPersisted from "components/SearchBoxPersisted";
import useGlobalContext from "hooks/globalData";
import useListTitle from "hooks/useListTitle";
import { useEffect, useState } from "react";
import { useIntl } from "react-intl";
import { useQuery } from "react-query";
import { useNavigate } from "react-router-dom";
import {
  AuthorityTypeAdminFilter,
  IAuthorityTypeListItem,
  ListRequestOfAuthorityTypeAdminFilter,
} from "services/KnowfullyAdminClient";
import contextStore from "stores/contextStore";
import persistanceStore from "stores/persistanceStore";

const SubmissionAuthorityList = () => {
  const { formatMessage: t } = useIntl();
  const navigate = useNavigate();
  const { submissionAuthorityService, submissionAuthorityDetailsUrl } =
    useGlobalContext();
  const [order, setOrder] = useState<TableSortType>("asc");
  const [orderBy, setOrderBy] = useState("name");
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(DEFAULT_ROWS_PER_PAGE);
  const { organizationId } = contextStore.useState();
  const { submissionAuthoritySearchTerm } = persistanceStore.useState();

  useListTitle("submissionAuthorities");

  useEffect(() => {
    setPage(0);
  }, [organizationId]);

  const listRequest = ListRequestOfAuthorityTypeAdminFilter.fromJS({
    pageNumber: page,
    pageSize: rowsPerPage,
    sortField: orderBy,
    sortDescending: order === "desc",
    parameters: AuthorityTypeAdminFilter.fromJS({
      searchText: submissionAuthoritySearchTerm || undefined,
    }),
  });

  const { data, isFetching } = useQuery(
    ["submissionAuthorityService.getAuthorityTypes", organizationId, listRequest],
    () => submissionAuthorityService.getAuthorityTypes(listRequest),
    {
      keepPreviousData: true,
    },
  );

  const submissionAuthorities = (
    (data?.data as IAuthorityTypeListItem[]) ?? []
  ).map((at) => ({
    ...at,
    id: at.id!,
  }));

  const cells: Array<TableCellType> = [
    {
      accessor: "name",
      type: "text",
      label: t({ id: "nameLabel" }),
    },
    {
      accessor: "isUploadSubmissionStatusAvailable",
      type: "boolean",
      label: t({ id: "uploadSubmissionStatusAvailableLabel" }),
    },
    {
      accessor: "submissionCutOffDays",
      type: "text",
      label: t({ id: "submissionCutOffDaysLabel" }),
    },
  ];

  const onClick = (id: string) => {
    navigate(submissionAuthorityDetailsUrl(id));
  };

  const handleSetPersistedSearchTerm = (searchText: string): void => {
    persistanceStore.update((s) => {
      s.submissionAuthoritySearchTerm = searchText;
    });
  };

  return (
    <Container>
      <SearchBoxPersisted
        setPersistedSearchTerm={handleSetPersistedSearchTerm}
        persistedSearchTerm={submissionAuthoritySearchTerm}
      />

      <LazyTable
        {...{
          rowCount: data?.totalRecords ?? 0,
          rows: submissionAuthorities,
          loading: isFetching,
          dense: true,
          title: t({ id: "submissionAuthorities" }),
          cells,
          page,
          setPage,
          order,
          setOrder,
          orderBy,
          setOrderBy,
          rowsPerPage,
          setRowsPerPage,
          onClick,
          contextMenuItems: [
            OpenLinkInNewTabContextMenuItem((id) =>
              submissionAuthorityDetailsUrl(id),
            ),
          ],
        }}
      />
    </Container>
  );
};

export default SubmissionAuthorityList;
